/**
 * FERP Portal - Regulatory Readiness Dashboard
 *
 * LIFE-CRITICAL: Shows regulatory compliance readiness score,
 * evidence gaps, and survey preparedness for the tenant.
 *
 * Uses regulatoryReadinessScore query from Phase 1b.
 * Data-driven: all metrics from backend, no hardcoded thresholds.
 *
 * Pattern source: RegulatoryRulesPage (tabs + stats cards)
 */

import { useMemo } from 'react';
import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Stack,
  Badge,
  ThemeIcon,
  Alert,
  Table,
  SimpleGrid,
  Loader,
  Button,
  RingProgress,
  Progress,
} from '@mantine/core';
import {
  IconShieldCheck,
  IconAlertTriangle,
  IconInfoCircle,
  IconRefresh,
  IconCheck,
  IconX,
  IconClipboardCheck,
  IconFileAlert,
} from '@tabler/icons-react';
import {
  useGetRegulatoryReadinessScoreQuery,
  GqlSafetyLevel,
} from '@/generated/graphql';
import { SAFETY_BADGE_CONFIG } from '@/config/regulatory-status';

function getScoreColor(pct: number): string {
  if (pct >= 80) return 'green';
  if (pct >= 60) return 'yellow';
  if (pct >= 40) return 'orange';
  return 'red';
}

export function ReadinessDashboard() {
  const { data, loading, error, refetch } = useGetRegulatoryReadinessScoreQuery();

  const score = useMemo(() => data?.regulatoryReadinessScore ?? null, [data]);

  const criticalGaps = useMemo(() => {
    if (!score) return [];
    return score.gaps.filter(
      (g) => g.safetyLevel === GqlSafetyLevel.CRITICAL && g.required,
    );
  }, [score]);

  const highGaps = useMemo(() => {
    if (!score) return [];
    return score.gaps.filter(
      (g) => g.safetyLevel === GqlSafetyLevel.HIGH && g.required,
    );
  }, [score]);

  // DRR-250: Sort gaps by citation count (highest first) for survey-driven prioritization
  const sortedGaps = useMemo(() => {
    if (!score) return [];
    return [...score.gaps].sort((a, b) => {
      // Citation count descending (nulls last)
      const aCount = a.citationCount ?? -1;
      const bCount = b.citationCount ?? -1;
      if (bCount !== aCount) return bCount - aCount;
      // Then by safety level (critical first)
      const safetyOrder = { CRITICAL: 0, HIGH: 1, STANDARD: 2, LOW: 3 };
      return (safetyOrder[a.safetyLevel] ?? 4) - (safetyOrder[b.safetyLevel] ?? 4);
    });
  }, [score]);

  if (error) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconAlertTriangle size={16} />} color="red" title="Error Loading Readiness">
          {error.message}
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Title order={2}>Survey Readiness</Title>
            <Text c="dimmed" size="sm">
              Regulatory compliance readiness score and evidence gaps
            </Text>
          </div>
          <Button
            leftSection={<IconRefresh size={16} />}
            variant="light"
            onClick={() => refetch()}
            loading={loading}
          >
            Refresh
          </Button>
        </Group>

        {loading ? (
          <Group justify="center" py="xl">
            <Loader size="lg" />
          </Group>
        ) : score ? (
          <>
            {/* Overall Score + Key Metrics */}
            <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }}>
              {/* Overall Readiness Ring */}
              <Card withBorder p="md">
                <Stack align="center" gap="xs">
                  <RingProgress
                    size={120}
                    thickness={12}
                    roundCaps
                    sections={[
                      { value: score.scorePct, color: getScoreColor(score.scorePct) },
                    ]}
                    label={
                      <Text ta="center" size="xl" fw={700}>
                        {Math.round(score.scorePct)}%
                      </Text>
                    }
                  />
                  <Text size="sm" c="dimmed">Overall Readiness</Text>
                </Stack>
              </Card>

              {/* Rules Coverage */}
              <Card withBorder p="md">
                <Group gap="xs">
                  <ThemeIcon variant="light" color="blue" size="lg">
                    <IconClipboardCheck size={20} />
                  </ThemeIcon>
                  <div>
                    <Text size="xl" fw={700}>
                      {score.fullyEvidencedRules}/{score.totalRules}
                    </Text>
                    <Text size="xs" c="dimmed">Rules Fully Evidenced</Text>
                  </div>
                </Group>
              </Card>

              {/* Critical Rules */}
              <Card withBorder p="md">
                <Group gap="xs">
                  <ThemeIcon variant="light" color="red" size="lg">
                    <IconShieldCheck size={20} />
                  </ThemeIcon>
                  <div>
                    <Text size="xl" fw={700}>
                      {score.criticalRulesEvidenced}/{score.criticalRulesTotal}
                    </Text>
                    <Text size="xs" c="dimmed">Critical Rules Covered</Text>
                  </div>
                </Group>
              </Card>

              {/* High Rules */}
              <Card withBorder p="md">
                <Group gap="xs">
                  <ThemeIcon variant="light" color="orange" size="lg">
                    <IconAlertTriangle size={20} />
                  </ThemeIcon>
                  <div>
                    <Text size="xl" fw={700}>
                      {score.highRulesEvidenced}/{score.highRulesTotal}
                    </Text>
                    <Text size="xs" c="dimmed">High Rules Covered</Text>
                  </div>
                </Group>
              </Card>
            </SimpleGrid>

            {/* Evidence Progress */}
            <Card withBorder p="md">
              <Stack gap="sm">
                <Text fw={500}>Evidence Coverage</Text>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    {score.totalEvidenceFound} of {score.totalEvidenceRequired} evidence items collected
                  </Text>
                  <Text size="sm" fw={500}>
                    {score.totalEvidenceRequired > 0
                      ? Math.round((score.totalEvidenceFound / score.totalEvidenceRequired) * 100)
                      : 0}%
                  </Text>
                </Group>
                <Progress
                  value={
                    score.totalEvidenceRequired > 0
                      ? (score.totalEvidenceFound / score.totalEvidenceRequired) * 100
                      : 0
                  }
                  color={getScoreColor(
                    score.totalEvidenceRequired > 0
                      ? (score.totalEvidenceFound / score.totalEvidenceRequired) * 100
                      : 0,
                  )}
                  size="lg"
                />
              </Stack>
            </Card>

            {/* Critical Gaps Alert */}
            {criticalGaps.length > 0 && (
              <Alert
                icon={<IconAlertTriangle size={16} />}
                color="red"
                title={`${criticalGaps.length} Critical Evidence Gaps`}
              >
                These CRITICAL safety rules are missing required evidence.
                Address these before any survey.
              </Alert>
            )}

            {/* Gaps Table — DRR-250: sorted by citation count (survey risk) */}
            {sortedGaps.length > 0 && (
              <Card withBorder p="md">
                <Stack gap="sm">
                  <Group gap="xs">
                    <IconFileAlert size={20} />
                    <Text fw={500}>Evidence Gaps ({sortedGaps.length})</Text>
                    <Text size="xs" c="dimmed">Sorted by MDH survey citation frequency</Text>
                  </Group>
                  <Table highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Rule ID</Table.Th>
                        <Table.Th>Citations</Table.Th>
                        <Table.Th>Safety</Table.Th>
                        <Table.Th>Missing Evidence</Table.Th>
                        <Table.Th>Required</Table.Th>
                        <Table.Th>Status</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {sortedGaps.map((gap) => {
                        const safetyConfig = SAFETY_BADGE_CONFIG[gap.safetyLevel]
                          ?? { color: 'gray', label: gap.safetyLevel };
                        const hasPartial = gap.foundDocumentIds.length > 0;

                        return (
                          <Table.Tr
                            key={`${gap.ruleId}-${gap.evidenceName}`}
                            style={{
                              borderLeft: gap.safetyLevel === GqlSafetyLevel.CRITICAL
                                ? '3px solid var(--mantine-color-red-6)'
                                : undefined,
                            }}
                          >
                            <Table.Td>
                              <Text size="sm" fw={500}>{gap.ruleId}</Text>
                            </Table.Td>
                            <Table.Td>
                              {gap.citationCount != null ? (
                                <Badge color={gap.citationCount >= 30 ? 'red' : gap.citationCount >= 20 ? 'orange' : 'gray'} variant="filled" size="sm">
                                  {gap.citationCount}
                                </Badge>
                              ) : (
                                <Text size="xs" c="dimmed">—</Text>
                              )}
                            </Table.Td>
                            <Table.Td>
                              <Badge color={safetyConfig.color} variant="light" size="sm">
                                {safetyConfig.label}
                              </Badge>
                            </Table.Td>
                            <Table.Td>
                              <Stack gap={0}>
                                <Text size="sm">{gap.evidenceName}</Text>
                                <Text size="xs" c="dimmed" lineClamp={1}>
                                  {gap.evidenceDescription}
                                </Text>
                              </Stack>
                            </Table.Td>
                            <Table.Td>
                              {gap.required ? (
                                <Badge color="red" variant="light" size="xs">Required</Badge>
                              ) : (
                                <Badge color="gray" variant="light" size="xs">Optional</Badge>
                              )}
                            </Table.Td>
                            <Table.Td>
                              {hasPartial ? (
                                <Group gap={4}>
                                  <IconCheck size={14} color="var(--mantine-color-yellow-6)" />
                                  <Text size="xs" c="yellow">Partial</Text>
                                </Group>
                              ) : (
                                <Group gap={4}>
                                  <IconX size={14} color="var(--mantine-color-red-6)" />
                                  <Text size="xs" c="red">Missing</Text>
                                </Group>
                              )}
                            </Table.Td>
                          </Table.Tr>
                        );
                      })}
                    </Table.Tbody>
                  </Table>
                </Stack>
              </Card>
            )}

            {/* All Clear */}
            {sortedGaps.length === 0 && (
              <Alert
                icon={<IconCheck size={16} />}
                color="green"
                title="Fully Compliant"
              >
                All approved regulatory rules have evidence attached.
                The tenant is survey-ready.
              </Alert>
            )}
          </>
        ) : (
          <Text c="dimmed" ta="center" py="xl">
            No readiness data available.
          </Text>
        )}
      </Stack>
    </Container>
  );
}

export default ReadinessDashboard;
